// Style schema (inlined to avoid function calls that break JSON serialization)
const styleSchema = {
  type: 'object',
  additionalProperties: false,
  properties: {
    fill: { type: 'string' },
    fontColor: { type: 'string' },
    fontWeight: { type: 'string', enum: ['normal', 'bold'] },
    fontSize: { type: 'number' },
    numberFormat: { type: 'string' },
    border: {
      type: 'object',
      additionalProperties: false,
      properties: {
        top: { type: 'string' },
        right: { type: 'string' },
        bottom: { type: 'string' },
        left: { type: 'string' },
        color: { type: 'string' },
      },
    },
  },
} as const;

// Scale value schema (defines a point on the scale - min, max, percent, etc.)
const scaleValueSchema = {
  type: 'object',
  required: ['type'],
  additionalProperties: false,
  properties: {
    type: {
      type: 'string',
      enum: ['number', 'min', 'max', 'percent', 'percentile', 'formula'],
    },
    // anyOf instead of oneOf because AJV coerceTypes can make values match both
    value: { anyOf: [{ type: 'number' }, { type: 'string' }] },
  },
} as const;

// Color scale point schema (scale value + color for gradient)
const colorScalePointSchema = {
  type: 'object',
  required: ['type', 'color'],
  additionalProperties: false,
  properties: {
    type: {
      type: 'string',
      enum: ['number', 'min', 'max', 'percent', 'percentile', 'formula'],
    },
    // anyOf instead of oneOf because AJV coerceTypes can make values match both
    value: { anyOf: [{ type: 'number' }, { type: 'string' }] },
    color: { type: 'string' },
  },
} as const;

// cellIs: compare cell value using operator
const cellIs = {
  type: 'object',
  required: ['type', 'range', 'operator', 'value'],
  additionalProperties: false,
  properties: {
    type: { type: 'string', enum: ['cellIs'] },
    range: { type: 'string' },
    operator: {
      type: 'string',
      enum: [
        'equal',
        'notEqual',
        'greaterThan',
        'greaterThanOrEqual',
        'lessThan',
        'lessThanOrEqual',
      ],
    },
    value: { type: 'string' },
    style: styleSchema,
  },
};

// cellIsBetween: compare cell value to a range
const cellIsBetween = {
  type: 'object',
  required: ['type', 'range', 'operator', 'from', 'to'],
  additionalProperties: false,
  properties: {
    type: { type: 'string', enum: ['cellIsBetween'] },
    range: { type: 'string' },
    operator: { type: 'string', enum: ['between', 'notBetween'] },
    from: { type: 'string' },
    to: { type: 'string' },
    style: styleSchema,
  },
};

// expression: custom formula that returns true/false
const expression = {
  type: 'object',
  required: ['type', 'range', 'formula'],
  additionalProperties: false,
  properties: {
    type: { type: 'string', enum: ['expression'] },
    range: { type: 'string' },
    formula: { type: 'string' },
    style: styleSchema,
  },
};

// containsText: check if cell contains/starts with/ends with text
const containsText = {
  type: 'object',
  required: ['type', 'range', 'text'],
  additionalProperties: false,
  properties: {
    type: { type: 'string', enum: ['containsText'] },
    range: { type: 'string' },
    operator: {
      type: 'string',
      enum: ['containsText', 'notContains', 'beginsWith', 'endsWith'],
    },
    text: { type: 'string' },
    style: styleSchema,
  },
};

// colorScale: gradient based on cell values
const colorScale = {
  type: 'object',
  required: ['type', 'range', 'colors'],
  additionalProperties: false,
  properties: {
    type: { type: 'string', enum: ['colorScale'] },
    range: { type: 'string' },
    colors: {
      type: 'array',
      items: colorScalePointSchema,
      minItems: 2,
      maxItems: 3,
    },
  },
};

// dataBar: bar chart visualization in cell
const dataBar = {
  type: 'object',
  required: ['type', 'range'],
  additionalProperties: false,
  properties: {
    type: { type: 'string', enum: ['dataBar'] },
    range: { type: 'string' },
    min: scaleValueSchema,
    max: scaleValueSchema,
    color: { type: 'string' },
    showValue: { type: 'boolean' },
  },
};

// SheetSpec JSON Schema
export const SheetSpecSchema = {
  type: 'object',
  required: ['version', 'sheets'],
  additionalProperties: false,
  properties: {
    version: { type: 'string', const: 'SheetSpec@2' },
    sheets: {
      type: 'array',
      minItems: 1,
      items: {
        type: 'object',
        required: ['name', 'cells'],
        additionalProperties: false,
        properties: {
          name: { type: 'string', minLength: 1 },
          cells: {
            type: 'array',
            items: {
              anyOf: [
                {
                  type: 'object',
                  required: ['a1', 'type', 'value'],
                  additionalProperties: false,
                  properties: {
                    a1: { type: 'string', pattern: '^[A-Z]+[1-9][0-9]*$' },
                    type: { type: 'string', const: 'text' },
                    value: { type: 'string' },
                    style: styleSchema,
                  },
                },
                {
                  type: 'object',
                  required: ['a1', 'type', 'value'],
                  additionalProperties: false,
                  properties: {
                    a1: { type: 'string', pattern: '^[A-Z]+[1-9][0-9]*$' },
                    type: { type: 'string', const: 'number' },
                    value: { type: 'number' },
                    style: styleSchema,
                  },
                },
                {
                  type: 'object',
                  required: ['a1', 'type', 'formula'],
                  additionalProperties: false,
                  properties: {
                    a1: { type: 'string', pattern: '^[A-Z]+[1-9][0-9]*$' },
                    type: { type: 'string', const: 'formula' },
                    formula: { type: 'string' },
                    style: styleSchema,
                  },
                },
              ],
            },
          },
          namedRanges: {
            type: 'array',
            items: {
              type: 'object',
              required: ['name', 'ref'],
              additionalProperties: false,
              properties: {
                name: { type: 'string' },
                ref: { type: 'string' }, // "A1:B12" or "Sheet!B2:M2"
              },
            },
          },
          // Conditional formatting rules (SheetSpec@2)
          conditionalFormats: {
            type: 'array',
            items: {
              anyOf: [
                cellIs,
                cellIsBetween,
                expression,
                containsText,
                colorScale,
                dataBar,
              ],
            },
          },
        },
      },
    },
    outputs: {
      type: 'array',
      items: {
        type: 'object',
        required: ['name', 'sheet', 'ref', 'metric'],
        additionalProperties: false,
        properties: {
          name: { type: 'string' },
          sheet: { type: 'string' },
          ref: { type: 'string' },
          metric: { type: 'string', enum: ['value', 'values'] },
        },
      },
    },
    rules: {
      type: 'object',
      additionalProperties: false,
      properties: {
        disallowVolatile: { type: 'boolean' },
        allowedFunctions: { type: 'array', items: { type: 'string' } },
      },
    },
  },
} as const;

// ============================================================================
// TypeScript Types
// ============================================================================

export type Cell = {
  a1: string;
  type: 'text' | 'number' | 'formula';
  value?: any;
  formula?: string;
  style?: CellStyle;
};

export type CellStyle = {
  fill?: string;
  fontColor?: string;
  fontWeight?: 'normal' | 'bold';
  fontSize?: number;
  numberFormat?: string;
  border?: {
    top?: string;
    right?: string;
    bottom?: string;
    left?: string;
    color?: string;
  };
};

// Scale value types for conditional formatting
export type ScaleValueType =
  | 'number'
  | 'min'
  | 'max'
  | 'percent'
  | 'percentile'
  | 'formula';

/**
 * Defines a point on a value scale for data bars.
 * - type: How to interpret the scale point (min/max of data, specific number, percentile, etc.)
 * - value: The value (required for 'number', 'percent', 'percentile', 'formula')
 *
 * Example: { type: 'percentile', value: 25 } = 25th percentile of the data
 */
export interface ScaleValue {
  type: ScaleValueType;
  value?: number | string;
}

/**
 * Defines a point on a color gradient scale.
 * Combines a scale position with a color for that position.
 *
 * Example: { type: 'min', color: '#FF0000' } = Red color at the minimum value
 * Example: { type: 'percentile', value: 50, color: '#FFFF00' } = Yellow at 50th percentile
 */
export interface ColorScalePoint extends ScaleValue {
  color: string;
}

// ============================================================================
// Conditional Formatting Rule Types
// ============================================================================

// TODO: colorScale and dataBar are fundamentally different from the other rule types.
// cellIs, cellIsBetween, expression, containsText apply CellStyle (fill, fontColor, etc.)
// colorScale and dataBar produce their own visual rendering (gradients, bars).
// Consider splitting these into separate schema concepts in the future:
//   - conditionalFormats: style-based rules (cellIs, expression, etc.)
//   - visualizations: colorScale, dataBar, iconSet, etc.

// Base interface for all conditional formatting rules
interface ConditionalFormatRuleBase {
  range: string; // Cell range the rule applies to (e.g., "A1:B10")
}

// Comparison operators for cellIs rules (single value comparison)
export type CellIsOperator =
  | 'equal'
  | 'notEqual'
  | 'greaterThan'
  | 'greaterThanOrEqual'
  | 'lessThan'
  | 'lessThanOrEqual';

// Operators for cellIsBetween rules (range comparison)
export type BetweenOperator = 'between' | 'notBetween';

// Text comparison operators for containsText rules
export type TextOperator =
  | 'containsText'
  | 'notContains'
  | 'beginsWith'
  | 'endsWith';

// Cell value comparison rule (single value)
export interface CellIsRule extends ConditionalFormatRuleBase {
  type: 'cellIs';
  operator: CellIsOperator;
  value: string;
  style: CellStyle;
}

// Cell value range comparison rule (between two values)
export interface CellIsBetweenRule extends ConditionalFormatRuleBase {
  type: 'cellIsBetween';
  operator: BetweenOperator;
  from: string;
  to: string;
  style: CellStyle;
}

// Formula-based rule
export interface ExpressionRule extends ConditionalFormatRuleBase {
  type: 'expression';
  formula: string;
  style: CellStyle;
}

// Text matching rule
export interface ContainsTextRule extends ConditionalFormatRuleBase {
  type: 'containsText';
  operator: TextOperator;
  text: string;
  style: CellStyle;
}

// Color scale rule (2 or 3 color gradient)
export interface ColorScaleRule extends ConditionalFormatRuleBase {
  type: 'colorScale';
  colors: ColorScalePoint[];
}

// Data bar rule
export interface DataBarRule extends ConditionalFormatRuleBase {
  type: 'dataBar';
  color: string;
  min?: ScaleValue; // Defaults to auto-detect from data
  max?: ScaleValue; // Defaults to auto-detect from data
  showValue?: boolean;
}

// Union of all conditional format rule types
export type ConditionalFormatRule =
  | CellIsRule
  | CellIsBetweenRule
  | ExpressionRule
  | ContainsTextRule
  | ColorScaleRule
  | DataBarRule;

export type SheetSpec = {
  version: 'SheetSpec@2';
  sheets: Array<{
    name: string;
    cells: Array<Cell>;
    namedRanges?: Array<{
      name: string;
      ref: string;
    }>;
    conditionalFormats?: Array<ConditionalFormatRule>;
  }>;
  outputs?: Array<{
    name: string;
    sheet: string;
    ref: string;
    metric: 'value' | 'values';
  }>;
  rules?: {
    disallowVolatile?: boolean;
    allowedFunctions?: string[];
  };
};
